import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';

import { RequireAuth } from '@/auth/RequireAuth';
import { RequireRole } from '@/auth/RequireRole';
import type { Role } from '@/auth/roles';
import { useAuth } from '@/auth/useAuth';
import { ForgotPasswordPage } from '@/features/auth/ForgotPasswordPage';
import { AcceptInvitationPage } from '@/features/auth/AcceptInvitationPage';
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage';
import { SignInPage } from '@/features/auth/SignInPage';
import { AccountPage } from '@/features/account/AccountPage';
import { ActivityPage } from '@/features/account/ActivityPage';
import { BillingPage } from '@/features/account/BillingPage';
import { ProfilePage } from '@/features/account/ProfilePage';
import { UsersPage } from '@/features/account/UsersPage';
import { ComponentsPage } from '@/features/components-gallery/ComponentsPage';
import { ArcBuilderPage } from '@/features/arc/ArcBuilderPage';
import { FundraisingPage } from '@/features/arc-links/FundraisingPage';
import { VolunteerPage } from '@/features/arc-links/VolunteerPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { CategoriesPage } from '@/features/documents/CategoriesPage';
import { DocumentsPage } from '@/features/documents/DocumentsPage';
import { ForbiddenPage } from '@/features/errors/ForbiddenPage';
import { NotFoundPage } from '@/features/errors/NotFoundPage';
import { PageEditorPage } from '@/features/pages/PageEditorPage';
import { PagesListPage } from '@/features/pages/PagesListPage';
import { SectionPlaceholder } from '@/features/placeholder/SectionPlaceholder';
import { CreateOrganizationPage } from '@/features/staff/CreateOrganizationPage';
import { OrganizationPage } from '@/features/staff/OrganizationPage';
import { OrganizationsPage } from '@/features/staff/OrganizationsPage';
import { StaffActivityPage } from '@/features/staff/StaffActivityPage';
import { InvitationsPage } from '@/features/supporters/InvitationsPage';
import { SupportersPage } from '@/features/supporters/SupportersPage';

import { ErrorRoute } from './layouts/ErrorRoute';
import { PortalLayout } from './layouts/PortalLayout';
import { landingFor } from './navigation';
import {
  ACCOUNT_ENTRIES,
  ANY_ORG_ROLE,
  CONTENT_SECTIONS,
  PATHS,
  STAFF_ONLY,
  SUPER_ADMIN_ONLY,
  type SectionEntry,
} from './paths';
import { ROUTES } from './routes';

/**
 * Every navigation entry gets a route, so nothing in the shell is a dead link.
 *
 * Generating them from `paths.ts`, which the navigation renders too, means the
 * two can never drift apart: a section added there is routable immediately.
 */
function placeholderRoutes(): RouteObject[] {
  // Seeded with the paths that already have a page, so a section is dropped
  // from here the moment it is built rather than generating a second route for
  // the same address.
  const seen = new Set<string>([
    ...PATHS.account,
    ROUTES.dashboard,
    ROUTES.arc.builder,
    ROUTES.arc.volunteer,
    ROUTES.arc.fundraising,
    ROUTES.documents.library,
    ROUTES.documents.upload,
    ROUTES.documents.categories,
    ROUTES.pages.custom,
    ROUTES.pages.new,
    ROUTES.supporters.all,
    ROUTES.supporters.invitations,
  ]);
  const open: RouteObject[] = [];
  // Grouped by the roles they need, so each set of routes sits under one guard
  // rather than one guard per route.
  const gated = new Map<string, { allow: readonly Role[]; routes: RouteObject[] }>();

  const add = (path: string, title: string, allow?: readonly Role[], hint?: string) => {
    if (seen.has(path)) return;
    seen.add(path);

    const route: RouteObject = {
      path: path.replace(/^\//, ''),
      element: <SectionPlaceholder title={title} description={hint} />,
    };

    if (!allow) {
      open.push(route);
      return;
    }

    const key = [...allow].sort().join(',');
    const group = gated.get(key) ?? { allow, routes: [] };
    group.routes.push(route);
    gated.set(key, group);
  };

  for (const section of CONTENT_SECTIONS as readonly SectionEntry[]) {
    add(section.path, section.label, section.allow, section.hint);
    // A sub-page and a "New ..." button belong to their section, so they take
    // its roles unless they state their own. Otherwise every child would have
    // to repeat them, and one forgotten repeat is an open door.
    section.children?.forEach((child) =>
      add(child.path, child.label, child.allow ?? section.allow),
    );
    if (section.action) {
      add(
        section.action.path,
        section.action.label,
        section.action.allow ?? section.allow,
      );
    }
  }

  ACCOUNT_ENTRIES.forEach((item) => add(item.path, item.label, item.allow));

  return [
    ...open,
    // Hiding an entry from the menu is not what stops somebody reaching it.
    // This is: a typed address, an old link and the back button all land here.
    ...[...gated.values()].map(({ allow, routes }) => ({
      element: <RequireRole allow={allow} />,
      children: routes,
    })),
  ];
}

/**
 * The front page sends each role where it belongs.
 *
 * A fixed redirect to the dashboard would send Activist staff to a page the
 * guard then refuses, so the first thing they saw after signing in would be a
 * refusal.
 */
function HomeRedirect() {
  const { user } = useAuth();
  return <Navigate to={landingFor(user?.role)} replace />;
}

/**
 * The route table, separate from the router built from it.
 *
 * Exported so tests can mount the real thing at a chosen address in a memory
 * router. Testing a hand-built tree instead would prove the guard works and
 * say nothing about whether the application uses it.
 */
export const routes: RouteObject[] = [
  // Signing in happens outside the portal frame: there is no navigation to show
  // somebody who is not signed in yet.
  { path: ROUTES.signIn, element: <SignInPage />, errorElement: <ErrorRoute /> },
  {
    path: ROUTES.forgotPassword,
    element: <ForgotPasswordPage />,
    errorElement: <ErrorRoute />,
  },
  {
    path: ROUTES.resetPassword,
    element: <ResetPasswordPage />,
    errorElement: <ErrorRoute />,
  },
  // Outside RequireAuth on purpose: somebody accepting an invitation has no
  // account to be authenticated against yet.
  {
    path: ROUTES.invitation(':token'),
    element: <AcceptInvitationPage />,
    errorElement: <ErrorRoute />,
  },

  {
    element: <RequireAuth />,
    errorElement: <ErrorRoute />,
    children: [
      {
        path: ROUTES.home,
        element: <PortalLayout />,
        children: [
          { index: true, element: <HomeRedirect /> },
          { path: ROUTES.forbidden.slice(1), element: <ForbiddenPage /> },
          {
            // Any signed-in role, on purpose: it is a reference page for the
            // mobile developer and QA, behind sign-in but behind no role.
            path: ROUTES.components.slice(1),
            element: <ComponentsPage />,
          },
          {
            // Activist staff only. Organization users are refused here, and the
            // staff navigation is not shown to them either.
            element: <RequireRole allow={STAFF_ONLY} />,
            children: [
              {
                path: ROUTES.staff.organizations.slice(1),
                element: <OrganizationsPage />,
              },
              {
                path: ROUTES.staff.newOrganization.slice(1),
                element: <CreateOrganizationPage />,
              },
              {
                // After the fixed "new" path above. React Router ranks a fixed
                // segment above a placeholder whatever the order here, but the
                // order says so too, for anyone reading it.
                path: ROUTES.staff.organization(':id').slice(1),
                element: <OrganizationPage />,
              },
              {
                path: ROUTES.staff.activity.slice(1),
                element: <StaffActivityPage />,
              },
            ],
          },
          {
            // The account rail, which belongs to the organization's own Super
            // Admin. Its roles are the same ones ACCOUNT_ENTRIES declares, so
            // the menu and the guard cannot disagree.
            element: <RequireRole allow={SUPER_ADMIN_ONLY} />,
            children: [
              {
                // A path, not this guard's index route: the guard has no path of
                // its own, so an index route here would answer at "/".
                path: ROUTES.account.account.slice(1),
                element: <AccountPage />,
              },
              {
                path: ROUTES.account.profile.slice(1),
                element: <ProfilePage />,
              },
              {
                path: ROUTES.account.billing.slice(1),
                element: <BillingPage />,
              },
              {
                path: ROUTES.account.activity.slice(1),
                element: <ActivityPage />,
              },
              {
                path: ROUTES.account.users.slice(1),
                element: <UsersPage />,
              },
            ],
          },
          {
            // The organization's own sections, as they are built. Activist
            // staff pass this guard only while viewing an organization.
            element: <RequireRole allow={ANY_ORG_ROLE} />,
            children: [
              {
                path: ROUTES.dashboard.slice(1),
                element: <DashboardPage />,
              },
              {
                path: ROUTES.arc.builder.slice(1),
                element: <ArcBuilderPage />,
              },
              {
                path: ROUTES.arc.volunteer.slice(1),
                element: <VolunteerPage />,
              },
              {
                path: ROUTES.arc.fundraising.slice(1),
                element: <FundraisingPage />,
              },
              {
                path: ROUTES.documents.library.slice(1),
                element: <DocumentsPage />,
              },
              {
                // The library with the upload dialog open (P2).
                path: ROUTES.documents.upload.slice(1),
                element: <DocumentsPage uploadOpen />,
              },
              {
                path: ROUTES.documents.categories.slice(1),
                element: <CategoriesPage />,
              },
              {
                path: ROUTES.pages.custom.slice(1),
                element: <PagesListPage />,
              },
              {
                // The page editor (P6); somebody who may only look is sent
                // from /pages/new back to the list by the editor (P22).
                path: ROUTES.pages.new.slice(1),
                element: <PageEditorPage />,
              },
              {
                // After the fixed "new" and "activistarc" paths, which React
                // Router ranks above a placeholder whatever the order.
                path: ROUTES.pages.edit(':id').slice(1),
                element: <PageEditorPage />,
              },
              {
                path: ROUTES.supporters.all.slice(1),
                element: <SupportersPage />,
              },
              {
                path: ROUTES.supporters.invitations.slice(1),
                element: <InvitationsPage />,
              },
            ],
          },
          ...placeholderRoutes(),
          // Last, and ranked below every other route by React Router whatever
          // its place: an address nothing else matches is answered here,
          // inside the frame, rather than by the bare error page outside it.
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
